import React, { useEffect, useState } from 'react';
import { farmApi, stableApi, boxApi } from '../services/api';
import { Farm, Stable, Box } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError, showSuccess } from '../utils/handleApiError';
import BoxDetails from '../components/BoxDetails';
import ConfirmModal from '../components/ConfirmModal';

const Farms: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddFarm, setShowAddFarm] = useState(false);
  const [showEditFarm, setShowEditFarm] = useState(false);
  const [editingFarm, setEditingFarm] = useState<Farm | null>(null);
  const [showAddStable, setShowAddStable] = useState(false);
  const [showAddBox, setShowAddBox] = useState(false);
  const [showEditStable, setShowEditStable] = useState(false);
  const [showEditBox, setShowEditBox] = useState(false);
  const [editingStable, setEditingStable] = useState<Stable | null>(null);
  const [editingBox, setEditingBox] = useState<Box | null>(null);
  const [selectedStable, setSelectedStable] = useState<Stable | null>(null);
  const [selectedBox, setSelectedBox] = useState<Box | null>(null);
  const [showBoxDetails, setShowBoxDetails] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ action: 'deleteFarm' | 'deleteStable' | 'deleteBox'; id: number } | null>(null);
  
  const [newFarm, setNewFarm] = useState<Partial<Farm>>({
    name: '',
    address: '',
    description: '',
  });

  const [newStable, setNewStable] = useState<Partial<Stable>>({
    name: '',
    capacity: 0,
    description: '',
  });

  const [newBox, setNewBox] = useState<Partial<Box>>({
    name: '',
    capacity: 0,
    size_sqm: undefined,
    description: '',
  });

  useEffect(() => {
    loadFarms();
  }, []);

  const loadFarms = async () => {
    try {
      setLoading(true);
      const response = await farmApi.getAll();
      setFarms(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error loading farms:', error);
      handleApiError(error, 'Hiba a telepek betöltése során');
      setFarms([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFarmDetails = async (farmId: number) => {
    try {
      const farm = await farmApi.getById(farmId);
      setSelectedFarm(farm);
      // Re-sync selectedStable and selectedBox from fresh nested data
      if (selectedStable) {
        const freshStable = farm.stables?.find(s => s.id === selectedStable.id) || null;
        setSelectedStable(freshStable);
        if (selectedBox && freshStable) {
          const freshBox = freshStable.boxes?.find(b => b.id === selectedBox.id) || null;
          setSelectedBox(freshBox);
        } else {
          setSelectedBox(null);
        }
      }
    } catch (error) {
      console.error('Error loading farm details:', error);
      handleApiError(error, 'Hiba a telep adatainak betöltése során');
    }
  };

  const handleAddFarm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await farmApi.create({ ...newFarm, company_id: user!.company_id  });
      showSuccess('Telep sikeresen létrehozva');
      setShowAddFarm(false);
      setNewFarm({ name: '', address: '', description: '' });
      loadFarms();
    } catch (error) {
      console.error('Error creating farm:', error);
      handleApiError(error, t('farm_creation_error', 'farms'));
    }
  };

  const handleAddStable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFarm) return;
    
    try {
      await stableApi.create(selectedFarm.id, newStable);
      showSuccess('Istálló sikeresen létrehozva');
      setShowAddStable(false);
      setNewStable({ name: '', capacity: 0, description: '' });
      loadFarmDetails(selectedFarm.id);
    } catch (error) {
      console.error('Error creating stable:', error);
      handleApiError(error, t('stable_creation_error', 'farms'));
    }
  };

  const handleEditFarm = (farm: Farm) => {
    setEditingFarm(farm);
    setShowEditFarm(true);
  };

  const handleUpdateFarm = async () => {
    if (!editingFarm) return;
    
    try {
      await farmApi.update(editingFarm.id, editingFarm);
      showSuccess('Telep sikeresen frissítve');
      setShowEditFarm(false);
      setEditingFarm(null);
      loadFarms();
      if (selectedFarm?.id === editingFarm.id) {
        setSelectedFarm({ ...editingFarm });
      }
    } catch (error) {
      console.error('Error updating farm:', error);
      handleApiError(error, t('farm_update_error', 'farms'));
    }
  };

  const handleDeleteFarm = async (farmId: number) => {
    try {
      await farmApi.delete(farmId);
      showSuccess('Telep sikeresen törölve');
      if (selectedFarm?.id === farmId) {
        setSelectedFarm(null);
        setSelectedStable(null);
      }
      loadFarms();
    } catch (error) {
      console.error('Error deleting farm:', error);
      handleApiError(error, t('farm_delete_error', 'farms'));
    }
  };

  const handleAddBox = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStable) return;
    
    try {
      if (!selectedFarm) return;
      await boxApi.create(selectedFarm.id, {
        ...newBox,
        stable_id: selectedStable.id
      });
      showSuccess('Box sikeresen létrehozva');
      setShowAddBox(false);
      setNewBox({ name: '', capacity: 0, size_sqm: undefined, description: '' });
      loadFarmDetails(selectedFarm.id);
    } catch (error: any) {
      console.error('Error creating box:', error);
      handleApiError(error, t('box_creation_error', 'farms'));
    }
  };

  const handleBoxClick = (box: Box) => {
    setSelectedBox({ ...box, stable: selectedStable || undefined });
    setShowBoxDetails(true);
  };

  const handleEditStable = (stable: Stable) => {
    setEditingStable(stable);
    setShowEditStable(true);
  };

  const handleUpdateStable = async () => {
    if (!editingStable || !selectedFarm) return;
    
    try {
      await stableApi.update(selectedFarm.id, editingStable.id, editingStable);
      showSuccess('Istálló sikeresen frissítve');
      setShowEditStable(false);
      setEditingStable(null);
      loadFarmDetails(selectedFarm.id);
    } catch (error: any) {
      console.error('Error updating stable:', error);
      handleApiError(error, t('stable_update_error', 'farms'));
    }
  };

  const handleDeleteStable = async (stableId: number) => {
    if (!selectedFarm) return;

    try {
      await stableApi.delete(selectedFarm.id, stableId);
      showSuccess('Istálló sikeresen törölve');
      if (selectedStable?.id === stableId) {
        setSelectedStable(null);
      }
      loadFarmDetails(selectedFarm.id);
    } catch (error) {
      console.error('Error deleting stable:', error);
      handleApiError(error, t('stable_delete_error', 'farms'));
    }
  };

  const handleEditBox = (box: Box) => {
    setEditingBox(box);
    setShowEditBox(true);
  };

  const handleUpdateBox = async () => {
    if (!editingBox || !selectedFarm) return;
    
    try {
      await boxApi.update(selectedFarm.id, editingBox.id, editingBox);
      showSuccess('Box sikeresen frissítve');
      setShowEditBox(false);
      setEditingBox(null);
      loadFarmDetails(selectedFarm.id);
    } catch (error: any) {
      console.error('Error updating box:', error);
      handleApiError(error, t('box_update_error', 'farms'));
    }
  };

  const handleDeleteBox = async (boxId: number) => {
    if (!selectedFarm) return;

    try {
      await boxApi.delete(selectedFarm.id, boxId);
      showSuccess('Box sikeresen törölve');
      loadFarmDetails(selectedFarm.id);
    } catch (error) {
      console.error('Error deleting box:', error);
      handleApiError(error, t('box_delete_error', 'farms'));
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { action, id } = confirmAction;
    try {
      if (action === 'deleteFarm') {
        await handleDeleteFarm(id);
      } else if (action === 'deleteStable') {
        await handleDeleteStable(id);
      } else if (action === 'deleteBox') {
        await handleDeleteBox(id);
      }
    } finally {
      setConfirmAction(null);
    }
  };

  const confirmModalProps = confirmAction
    ? {
        deleteFarm: {
          title: 'Telep törlése',
          message: t('confirm_delete_farm', 'farms'),
        },
        deleteStable: {
          title: 'Istálló törlése',
          message: t('confirm_delete_stable', 'farms'),
        },
        deleteBox: {
          title: 'Box törlése',
          message: t('confirm_delete_box', 'farms'),
        },
      }[confirmAction.action]
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('farms_management', 'farms')}</h1>
            <p className="mt-2 text-gray-600">
              {t('farms_management_desc', 'farms')}
            </p>
          </div>
          <button
            onClick={() => setShowAddFarm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('new_farm', 'farms')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Telepek listája */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('farms_list', 'farms')} ({farms.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {farms.map((farm) => (
              <div
                key={farm.id}
                className={`p-4 hover:bg-gray-50 ${
                  selectedFarm?.id === farm.id ? 'bg-green-50 border-r-4 border-green-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div onClick={() => loadFarmDetails(farm.id)} className="flex-1 cursor-pointer">
                    <h4 className="text-sm font-medium text-gray-900">{farm.name}</h4>
                    <p className="text-sm text-gray-500">{farm.address}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {farm.stables?.length || 0} {t('stables', 'farms')}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('active', 'status')}
                      </span>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditFarm(farm); }}
                        className="text-xs text-yellow-600 hover:text-yellow-900"
                        title={t('edit', 'farms')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmAction({ action: 'deleteFarm', id: farm.id }); }}
                        className="text-xs text-red-600 hover:text-red-900"
                        title={t('delete', 'action')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {farms.length === 0 && (
              <div className="p-4 text-center text-gray-500">
                <p>{t('no_farms_yet', 'farms')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Istállók */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('stables_list', 'farms')} {selectedFarm ? `(${selectedFarm.stables?.length || 0})` : ''}
              </h3>
              {selectedFarm && (
                <button
                  onClick={() => setShowAddStable(true)}
                  className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
                >
                  + {t('stable', 'farms')}
                </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {selectedFarm ? (
              selectedFarm.stables && selectedFarm.stables.length > 0 ? (
                selectedFarm.stables.map((stable) => (
                  <div
                    key={stable.id}
                    className={`p-4 hover:bg-gray-50 ${
                      selectedStable?.id === stable.id ? 'bg-blue-50 border-r-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div onClick={() => setSelectedStable(stable)} className="flex-1 cursor-pointer">
                        <h4 className="text-sm font-medium text-gray-900">{stable.name}</h4>
                        <p className="text-sm text-gray-500">{t('capacity', 'farms')}: {stable.capacity} {t('animals', 'farms')}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {stable.boxes?.length || 0} {t('boxes', 'farms')} • Box kapacitás: {stable.total_box_capacity || 0}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="mb-2">
                          <span className="text-xs text-gray-500">
                            Állatok: {stable.current_animal_count || 0}/{stable.total_box_capacity || 0}
                          </span>
                          <br />
                          <span className="text-xs text-gray-400">
                            Istálló: {stable.total_box_capacity || 0}/{stable.capacity}
                          </span>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditStable(stable); }}
                            className="text-xs text-yellow-600 hover:text-yellow-900"
                            title={t('edit', 'farms')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmAction({ action: 'deleteStable', id: stable.id }); }}
                            className="text-xs text-red-600 hover:text-red-900"
                            title={t('delete', 'action')}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <p>{t('no_stables_in_farm', 'farms')}</p>
                  <button
                    onClick={() => setShowAddStable(true)}
                    className="mt-2 text-blue-600 hover:text-blue-500 text-sm"
                  >
                    {t('add_first_stable', 'farms')}
                  </button>
                </div>
              )
            ) : (
              <div className="p-4 text-center text-gray-500">
                <p>{t('select_farm_left', 'farms')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Boxok */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('boxes', 'farms')} {selectedStable ? `(${selectedStable.boxes?.length || 0})` : ''}
              </h3>
              {selectedStable && (
                <button
                  onClick={() => setShowAddBox(true)}
                  className="bg-purple-600 text-white px-3 py-1 rounded-md text-sm hover:bg-purple-700 transition-colors"
                >
                  + {t('box', 'farms')}
                </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {selectedStable ? (
              selectedStable.boxes && selectedStable.boxes.length > 0 ? (
                selectedStable.boxes.map((box) => (
                  <div key={box.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div onClick={() => handleBoxClick(box)} className="flex-1 cursor-pointer">
                        <h4 className="text-sm font-medium text-gray-900 hover:text-blue-600">{box.name}</h4>
                        <p className="text-sm text-gray-500">{t('capacity', 'farms')}: {box.capacity} {t('animals', 'farms')}</p>
                        {box.size_sqm && (
                          <p className="text-xs text-gray-400">{t('area', 'farms')}: {box.size_sqm} m²</p>
                        )}
                        <p className="text-xs text-blue-500 mt-1">{t('click_for_details', 'farms')}</p>
                      </div>
                      <div className="text-right">
                        <div className="mb-2">
                          <span className="text-xs text-gray-500">
                            {box.current_animal_count || 0}/{box.capacity}
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            (box.current_animal_count || 0) >= box.capacity 
                              ? 'bg-red-100 text-red-800' 
                              : (box.current_animal_count || 0) > box.capacity * 0.8 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                          }`}>
                            {(box.current_animal_count || 0) >= box.capacity 
                              ? t('full', 'farms') 
                              : (box.current_animal_count || 0) > box.capacity * 0.8 
                                ? t('almost_full', 'farms')
                                : t('available', 'farms')}
                          </span>
                        </div>
                        <div className="flex space-x-1 justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditBox(box); }}
                            className="text-xs text-yellow-600 hover:text-yellow-900"
                            title={t('edit', 'farms')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmAction({ action: 'deleteBox', id: box.id }); }}
                            className="text-xs text-red-600 hover:text-red-900"
                            title={t('delete', 'action')}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <p>{t('no_boxes_in_stable', 'farms')}</p>
                  <button
                    onClick={() => setShowAddBox(true)}
                    className="mt-2 text-purple-600 hover:text-purple-500 text-sm"
                  >
                    {t('add_first_box', 'farms')}
                  </button>
                </div>
              )
            ) : (
              <div className="p-4 text-center text-gray-500">
                <p>{t('select_stable_middle', 'farms')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Farm Modal */}
      {showAddFarm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_new_farm', 'farms')}</h3>
            <form onSubmit={handleAddFarm} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('farm_name', 'form')} *</label>
                <input
                  type="text"
                  required
                  value={newFarm.name}
                  onChange={(e) => setNewFarm({ ...newFarm, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('address', 'form')} *</label>
                <input
                  type="text"
                  required
                  value={newFarm.address}
                  onChange={(e) => setNewFarm({ ...newFarm, address: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={newFarm.description}
                  onChange={(e) => setNewFarm({ ...newFarm, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddFarm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('add', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Farm Modal */}
      {showEditFarm && editingFarm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('edit_farm', 'farms')}</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateFarm(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('farm_name', 'form')} *</label>
                <input
                  type="text"
                  required
                  value={editingFarm.name}
                  onChange={(e) => setEditingFarm({ ...editingFarm, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('address', 'form')} *</label>
                <input
                  type="text"
                  required
                  value={editingFarm.address}
                  onChange={(e) => setEditingFarm({ ...editingFarm, address: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={editingFarm.description || ''}
                  onChange={(e) => setEditingFarm({ ...editingFarm, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowEditFarm(false); setEditingFarm(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Stable Modal */}
      {showAddStable && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_new_stable', 'farms')}</h3>
            <form onSubmit={handleAddStable} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('stable_name', 'farms')} *</label>
                <input
                  type="text"
                  required
                  value={newStable.name}
                  onChange={(e) => setNewStable({ ...newStable, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('capacity_animals', 'farms')} *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={newStable.capacity}
                  onChange={(e) => setNewStable({ ...newStable, capacity: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={newStable.description}
                  onChange={(e) => setNewStable({ ...newStable, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddStable(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {t('add', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Box Modal */}
      {showAddBox && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_new_box', 'farms')}</h3>
            <form onSubmit={handleAddBox} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('box_name', 'farms')} *</label>
                <input
                  type="text"
                  required
                  value={newBox.name}
                  onChange={(e) => setNewBox({ ...newBox, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('capacity_animals', 'farms')} *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={newBox.capacity}
                    onChange={(e) => setNewBox({ ...newBox, capacity: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('area_sqm', 'farms')}</label>
                  <input
                    type="number"
                    min="1"
                    value={newBox.size_sqm || ''}
                    onChange={(e) => setNewBox({ ...newBox, size_sqm: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={newBox.description}
                  onChange={(e) => setNewBox({ ...newBox, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddBox(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  {t('add', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Stable Modal */}
      {showEditStable && editingStable && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('edit_stable', 'farms')}</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateStable(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('stable_name', 'farms')} *</label>
                <input
                  type="text"
                  required
                  value={editingStable.name}
                  onChange={(e) => setEditingStable({ ...editingStable, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('capacity_animals', 'farms')} *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={editingStable.capacity}
                  onChange={(e) => setEditingStable({ ...editingStable, capacity: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={editingStable.description || ''}
                  onChange={(e) => setEditingStable({ ...editingStable, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowEditStable(false); setEditingStable(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Box Modal */}
      {showEditBox && editingBox && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('edit_box', 'farms')}</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateBox(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('box_name', 'farms')} *</label>
                <input
                  type="text"
                  required
                  value={editingBox.name}
                  onChange={(e) => setEditingBox({ ...editingBox, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('capacity_animals', 'farms')} *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={editingBox.capacity}
                    onChange={(e) => setEditingBox({ ...editingBox, capacity: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('area_sqm', 'farms')}</label>
                  <input
                    type="number"
                    min="1"
                    value={editingBox.size_sqm || ''}
                    onChange={(e) => setEditingBox({ ...editingBox, size_sqm: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={editingBox.description || ''}
                  onChange={(e) => setEditingBox({ ...editingBox, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowEditBox(false); setEditingBox(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Box Details Modal */}
      {showBoxDetails && selectedBox && (
        <BoxDetails
          box={selectedBox}
          onClose={() => {
            setShowBoxDetails(false);
            setSelectedBox(null);
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!confirmAction}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        title={confirmModalProps?.title || ''}
        message={confirmModalProps?.message}
        confirmLabel="Törlés"
        variant="danger"
      />
    </div>
  );
};

export default Farms;